import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Typography } from '@alfalab/core-components/typography';
import { createCn } from 'bem-react-classname';
import config from 'config';
import { trackInAnalytics } from 'newclick-alfa-metrics';

import { Link } from '@components/Link';
import { getMobileAppIdForIosVersion, resolveIosDeepLink } from '@utils/deeplink';
import { getIsAndroid } from '@utils/helpers';
import { RootState } from '@utils/types';

import './ButtonGoToApp.scss';

const DEEP_LINK_FOR_SPLIT = 'alfabank://payments_hub';
const DEEP_LINK_FOR_SPLIT_IOS = 'alfabank:///dashboard/payments_hub';

const showButtonGoToAppForSplit: boolean = config.get('client.showButtonGoToAppForSplit');
const showButtonGoToAppForPool: boolean = config.get('client.showButtonGoToAppForPool');

const cn = createCn('button-go-to-app');

const background = {
    background: `url('${config.get('client.contextRoot')}/public/image-button-go-to-app.png') #b7dfff no-repeat 7px`
};

interface Props {
    appVersion?: string;
}

export const ButtonGoToApp: React.VFC<Props> = ({ appVersion }) => {
    const [deepLink, setDeepLink] = useState(DEEP_LINK_FOR_SPLIT);
    const [onShow, setShow] = useState(false);
    const { cardId, type, name } = useSelector((state: RootState) => state.request.initializeApplicationRequest.result);

    const router = useRouter();

    const handleClick = () => {
        trackInAnalytics('GO_TO_APPLICATION');
    };

    const getDeepLink = useCallback(() => {
        if (type === 'MONEY_POOL' && getIsAndroid()) {
            return `alfabank://money_pool_transfer?cardId=${+cardId}&name=${name}`;
        }
        if (type === 'MONEY_POOL' && !getIsAndroid()) {
            const url = `alfabank:///dashboard/money_pool_transfer?cardId=${+cardId}&name=${name}`;

            try {
                const { applicationId } = router.query;

                const currentApplicationId = (applicationId || config.server?.mobileAppId) as string;
                const appIdIosVersion = getMobileAppIdForIosVersion(appVersion, currentApplicationId);

                const iosDeeplink = resolveIosDeepLink(url, appIdIosVersion);

                return iosDeeplink;
            } catch (error) {
                return url;
            }
        }

        if (!getIsAndroid()) {
            return DEEP_LINK_FOR_SPLIT_IOS;
        }

        return DEEP_LINK_FOR_SPLIT;
    }, [appVersion, cardId, name, router.query, type]);

    useEffect(() => {
        setDeepLink(getDeepLink());
    }, [getDeepLink]);

    useEffect(() => {
        if (type === 'MONEY_POOL') {
            setShow(showButtonGoToAppForPool);
        }
        if (type === 'SPLIT_PAYMENT') {
            setShow(showButtonGoToAppForSplit);
        }
    }, [type]);

    return (
        onShow && (
            <Link href={deepLink}>
                <div className={cn()} onClick={handleClick}>
                    <div className={cn('icon')} style={background} />
                    <Typography.Text view='primary-medium'>
                        Перевести из приложения
                        <br />
                        «Альфа-Банка»
                    </Typography.Text>
                </div>
            </Link>
        )
    );
};
